/**
 * Centralized Status Manager for Companion Profiles
 * PairUpToday Platform
 *
 * Maps Firestore document completion state and verification workflows
 * to authoritative lifecycle statuses:
 * - 'draft'
 * - 'documents_incomplete'
 * - 'documents_uploaded'
 * - 'under_review'
 * - 'verified'
 * - 'rejected'
 * - 'suspended'
 */

import { doc, getDoc, setDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  CompanionProfileLifecycleStatus,
  SupportedDocumentType,
  CompanionDocumentEntry,
  DocumentValidationResult,
  validateCompanionDocuments,
  resolveActiveDocumentRequirements,
  DOCUMENT_REQUIREMENTS_REGISTRY,
  hasValidUploadedData,
  isPartUploading,
  normalizeDocumentType,
} from './documentValidation';
import {
  CompanionDocumentRecord,
  CompanionVerificationProfile,
} from '../types/verification.types';

export type { CompanionProfileLifecycleStatus };

export interface CompanionDocumentCompletionReport {
  profileStatus: CompanionProfileLifecycleStatus;
  isValid: boolean;
  totalRequiredDocuments: number;
  totalRequiredSides: number;
  uploadedSidesCount: number;
  missingSidesCount: number;
  uploadingSidesCount: number;
  completionPercentage: number;
  missingItems: {
    documentId: string;
    documentTitle: string;
    side: 'front' | 'back';
    label: string;
    reason: string;
  }[];
  uploadingItems: {
    documentId: string;
    documentTitle: string;
    side: 'front' | 'back';
    label: string;
  }[];
  summaryMessage: string;
  allowedNextActions: string[];
}

export interface LifecycleStatusMetadata {
  status: CompanionProfileLifecycleStatus;
  title: string;
  shortLabel: string;
  badgeLabel: string;
  badgeStyle: string;
  dotColor: string;
  description: string;
  actionRequired: boolean;
  nextStep: string;
  canAcceptBookings: boolean;
  canReceivePayouts: boolean;
}

export const LIFECYCLE_STATUS_METADATA: Record<
  CompanionProfileLifecycleStatus,
  LifecycleStatusMetadata
> = {
  draft: {
    status: 'draft',
    title: 'Draft Profile',
    shortLabel: 'Draft',
    badgeLabel: 'DRAFT PROFILE',
    badgeStyle: 'bg-slate-900 border-slate-700 text-slate-300',
    dotColor: 'bg-slate-400',
    description: 'Initial profile creation in progress. Complete basic companion details and proceed to document verification.',
    actionRequired: true,
    nextStep: 'Complete profile information & bio',
    canAcceptBookings: false,
    canReceivePayouts: false,
  },
  documents_incomplete: {
    status: 'documents_incomplete',
    title: 'Documents Incomplete',
    shortLabel: 'Incomplete',
    badgeLabel: 'DOCUMENTS INCOMPLETE',
    badgeStyle: 'bg-amber-950/90 border-amber-600 text-amber-300',
    dotColor: 'bg-amber-400 animate-pulse',
    description: 'Mandatory identity sides or pages are missing. Both front and back are required for UIDAI Aadhaar and Driving Licences.',
    actionRequired: true,
    nextStep: 'Upload missing document sides (e.g. Aadhaar Back)',
    canAcceptBookings: false,
    canReceivePayouts: false,
  },
  documents_uploaded: {
    status: 'documents_uploaded',
    title: 'Documents Uploaded',
    shortLabel: 'Uploaded',
    badgeLabel: 'DOCUMENTS UPLOADED',
    badgeStyle: 'bg-blue-950/90 border-blue-600 text-blue-300',
    dotColor: 'bg-blue-400',
    description: 'All mandatory documents have been uploaded successfully. Ready to submit for platform compliance review.',
    actionRequired: true,
    nextStep: 'Submit documents for admin review',
    canAcceptBookings: false,
    canReceivePayouts: false,
  },
  under_review: {
    status: 'under_review',
    title: 'Under Admin Review',
    shortLabel: 'In Review',
    badgeLabel: 'UNDER REVIEW',
    badgeStyle: 'bg-purple-950/90 border-purple-600 text-purple-300',
    dotColor: 'bg-purple-400 animate-pulse',
    description: 'Your uploaded government documentation is currently being reviewed by PairUpToday verification team.',
    actionRequired: false,
    nextStep: 'Await admin verification verdict',
    canAcceptBookings: false,
    canReceivePayouts: false,
  },
  verified: {
    status: 'verified',
    title: 'Verified & Active',
    shortLabel: 'Verified',
    badgeLabel: 'VERIFIED COMPANION',
    badgeStyle: 'bg-emerald-950/90 border-emerald-600 text-emerald-300',
    dotColor: 'bg-emerald-400',
    description: 'Identity verification approved. Companion profile is fully certified and eligible for pairings and payouts.',
    actionRequired: false,
    nextStep: 'Accept pairings and earn payouts',
    canAcceptBookings: true,
    canReceivePayouts: true,
  },
  rejected: {
    status: 'rejected',
    title: 'Verification Rejected',
    shortLabel: 'Rejected',
    badgeLabel: 'RE-UPLOAD REQUIRED',
    badgeStyle: 'bg-rose-950/90 border-rose-600 text-rose-300',
    dotColor: 'bg-rose-400 animate-ping',
    description: 'Verification could not be approved. Please review administrator feedback and re-upload clear document copies.',
    actionRequired: true,
    nextStep: 'Re-upload corrected document images',
    canAcceptBookings: false,
    canReceivePayouts: false,
  },
  suspended: {
    status: 'suspended',
    title: 'Account Suspended',
    shortLabel: 'Suspended',
    badgeLabel: 'SUSPENDED',
    badgeStyle: 'bg-red-950/90 border-red-700 text-red-400',
    dotColor: 'bg-red-500',
    description: 'This companion account has been suspended due to policy violations or safety investigations. Contact support.',
    actionRequired: true,
    nextStep: 'Contact PairUpToday Safety & Support',
    canAcceptBookings: false,
    canReceivePayouts: false,
  },
};

/**
 * Maps Firestore document completion state and companion profile context
 * into the authoritative CompanionProfileLifecycleStatus.
 */
export function mapFirestoreStateToLifecycleStatus(input: {
  documents?: Record<string, any>;
  isSuspended?: boolean;
  accountStatus?: string;
  adminVerdict?: 'verified' | 'rejected' | 'suspended' | null;
  rejectionReason?: string | null;
  isVerified?: boolean;
  verificationStatus?: string;
  identityStatus?: string;
  isProfileInfoComplete?: boolean;
  selectedPrimaryDocType?: string;
  selectedServices?: string[];
  isDriverService?: boolean;
}): CompanionProfileLifecycleStatus {
  // 1. Suspension takes highest precedence
  if (
    input.isSuspended === true ||
    input.accountStatus?.toLowerCase() === 'suspended' ||
    input.adminVerdict === 'suspended' ||
    input.verificationStatus === 'suspended'
  ) {
    return 'suspended';
  }

  // 2. Explicit Admin Rejection or document re-upload required
  if (
    input.adminVerdict === 'rejected' ||
    input.verificationStatus === 'rejected' ||
    input.identityStatus === 'reupload_required' ||
    Boolean(input.rejectionReason && input.rejectionReason.trim().length > 0)
  ) {
    return 'rejected';
  }

  // 3. Admin Verification Approved
  if (
    input.adminVerdict === 'verified' ||
    input.isVerified === true ||
    input.verificationStatus === 'verified' ||
    input.identityStatus === 'verified'
  ) {
    return 'verified';
  }

  // 4. Draft profile state (basic info incomplete)
  if (input.isProfileInfoComplete === false) {
    return 'draft';
  }

  // 5. Check document completion state using centralized validation utility
  const validation = validateCompanionDocuments(input.documents || {}, {
    selectedPrimaryDocType: input.selectedPrimaryDocType || 'aadhaar',
    selectedServices: input.selectedServices,
    isDriverService: input.isDriverService,
    isProfileInfoComplete: input.isProfileInfoComplete ?? true,
    verificationStarted:
      input.verificationStatus === 'under_review' ||
      input.verificationStatus === 'submitted' ||
      input.identityStatus === 'pending',
    currentAdminVerdict: input.adminVerdict,
  });

  // If any mandatory side is missing or uploading, status is strictly 'documents_incomplete'
  if (!validation.isValid || validation.missingItems.length > 0 || validation.uploadingItems.length > 0) {
    return 'documents_incomplete';
  }

  // 6. If all mandatory documents are uploaded:
  // Did companion submit for review or is verification in progress?
  if (
    input.verificationStatus === 'under_review' ||
    input.verificationStatus === 'submitted' ||
    input.identityStatus === 'pending'
  ) {
    return 'under_review';
  }

  // Otherwise, all mandatory documents are uploaded and awaiting formal submission
  return 'documents_uploaded';
}

/**
 * Evaluates companion document completion state and produces a comprehensive report.
 */
export function evaluateCompanionDocumentCompletion(input: {
  documents: Record<string, any>;
  selectedPrimaryDocType?: string;
  selectedServices?: string[];
  isDriverService?: boolean;
  isProfileInfoComplete?: boolean;
  adminVerdict?: 'verified' | 'rejected' | 'suspended' | null;
  rejectionReason?: string | null;
  verificationStatus?: string;
  identityStatus?: string;
  isSuspended?: boolean;
}): CompanionDocumentCompletionReport {
  const activeReqs = resolveActiveDocumentRequirements({
    selectedPrimaryDocType: input.selectedPrimaryDocType || 'aadhaar',
    selectedServices: input.selectedServices,
    isDriverService: input.isDriverService,
  });

  let totalRequiredSides = 0;
  let uploadedSidesCount = 0;
  let missingSidesCount = 0;
  let uploadingSidesCount = 0;

  const missingItems: CompanionDocumentCompletionReport['missingItems'] = [];
  const uploadingItems: CompanionDocumentCompletionReport['uploadingItems'] = [];

  for (const req of activeReqs) {
    const docEntry = input.documents[req.id] || input.documents[normalizeDocumentType(req.id)];

    for (const part of req.parts) {
      if (!part.required) continue;
      totalRequiredSides += 1;

      const partData = part.side === 'front' ? docEntry?.front : docEntry?.back;

      if (isPartUploading(partData)) {
        uploadingSidesCount += 1;
        uploadingItems.push({
          documentId: req.id,
          documentTitle: req.title,
          side: part.side,
          label: part.label,
        });
      } else if (hasValidUploadedData(partData)) {
        uploadedSidesCount += 1;
      } else {
        missingSidesCount += 1;
        const shortName = part.side === 'front' ? 'Front' : 'Back';
        missingItems.push({
          documentId: req.id,
          documentTitle: req.title,
          side: part.side,
          label: part.label,
          reason: `${req.title} ${shortName}: Missing`,
        });
      }
    }
  }

  const isValid = missingSidesCount === 0 && uploadingSidesCount === 0;
  const completionPercentage =
    totalRequiredSides > 0
      ? Math.round((uploadedSidesCount / totalRequiredSides) * 100)
      : 100;

  const profileStatus = mapFirestoreStateToLifecycleStatus({
    documents: input.documents,
    isSuspended: input.isSuspended,
    adminVerdict: input.adminVerdict,
    rejectionReason: input.rejectionReason,
    verificationStatus: input.verificationStatus,
    identityStatus: input.identityStatus,
    isProfileInfoComplete: input.isProfileInfoComplete,
    selectedPrimaryDocType: input.selectedPrimaryDocType,
    selectedServices: input.selectedServices,
    isDriverService: input.isDriverService,
  });

  let summaryMessage = 'All required documents verified successfully.';
  if (uploadingSidesCount > 0) {
    summaryMessage = `Please wait for ${uploadingSidesCount} upload(s) to finish.`;
  } else if (missingSidesCount > 0) {
    const firstMissing = missingItems[0];
    summaryMessage = `${firstMissing.documentTitle} ${firstMissing.side.toUpperCase()}: Missing. Please upload all required document sides.`;
  }

  const allowedNextActions: string[] = [];
  if (profileStatus === 'documents_incomplete') {
    allowedNextActions.push('UPLOAD_DOCUMENT', 'VIEW_STATUS');
  } else if (profileStatus === 'documents_uploaded') {
    allowedNextActions.push('SUBMIT_FOR_REVIEW', 'REPLACE_DOCUMENT');
  } else if (profileStatus === 'under_review') {
    allowedNextActions.push('VIEW_REVIEW_PROGRESS');
  } else if (profileStatus === 'verified') {
    allowedNextActions.push('ACCEPT_BOOKINGS', 'VIEW_PAYOUTS');
  } else if (profileStatus === 'rejected') {
    allowedNextActions.push('REUPLOAD_DOCUMENTS', 'VIEW_REJECTION_REASON');
  } else if (profileStatus === 'suspended') {
    allowedNextActions.push('CONTACT_SUPPORT');
  } else {
    allowedNextActions.push('COMPLETE_PROFILE');
  }

  return {
    profileStatus,
    isValid,
    totalRequiredDocuments: activeReqs.length,
    totalRequiredSides,
    uploadedSidesCount,
    missingSidesCount,
    uploadingSidesCount,
    completionPercentage,
    missingItems,
    uploadingItems,
    summaryMessage,
    allowedNextActions,
  };
}

/**
 * Centralized Firestore updater that recalculates the companion's lifecycle status
 * from its documents and profile context, then persists the updated fields to Firestore:
 * - 'companions/{companionId}'
 * - 'users/{userId}' (if userId is available or matches companionId)
 */
export async function syncCompanionLifecycleInFirestore(
  companionId: string,
  options?: {
    userId?: string;
    documents?: Record<string, any>;
    context?: Partial<{
      isProfileInfoComplete?: boolean;
      selectedPrimaryDocType?: string;
      selectedServices?: string[];
      isDriverService?: boolean;
      adminVerdict?: 'verified' | 'rejected' | 'suspended' | null;
      rejectionReason?: string | null;
      verificationStarted?: boolean;
      verificationStatus?: string;
      identityStatus?: string;
      isSuspended?: boolean;
    }>;
  }
): Promise<{
  profileStatus: CompanionProfileLifecycleStatus;
  report: CompanionDocumentCompletionReport;
}> {
  if (!companionId) {
    throw new Error('companionId is required for syncCompanionLifecycleInFirestore');
  }

  // 1. Resolve documents: use provided or fetch from subcollection
  let documentsMap = options?.documents;
  if (!documentsMap) {
    documentsMap = {};
    try {
      const docsColRef = collection(db, 'companions', companionId, 'documents');
      const snap = await getDocs(docsColRef);
      snap.forEach((d) => {
        const data = d.data();
        if (data.documentType) {
          documentsMap![data.documentType] = data;
        }
      });
    } catch (e) {
      console.warn('Could not read documents from Firestore, using local cache:', e);
      try {
        const cached = localStorage.getItem(`pairup_verification_docs_${companionId}`);
        if (cached) documentsMap = JSON.parse(cached);
      } catch {}
    }
  }

  // 2. Fetch root companion doc if context not fully provided
  let existingCompData: any = {};
  try {
    const compRef = doc(db, 'companions', companionId);
    const snap = await getDoc(compRef);
    if (snap.exists()) {
      existingCompData = snap.data();
    }
  } catch (e) {
    console.warn('Could not read companion profile from Firestore:', e);
  }

  // 3. Merge context
  const mergedContext = {
    selectedPrimaryDocType:
      options?.context?.selectedPrimaryDocType ||
      existingCompData.primaryDocType ||
      existingCompData.idType ||
      'aadhaar',
    selectedServices:
      options?.context?.selectedServices ||
      existingCompData.services ||
      [],
    isDriverService:
      options?.context?.isDriverService ??
      Boolean(existingCompData.isDriverApproved || existingCompData.services?.some((s: string) => typeof s === 'string' && s.toLowerCase().includes('driver'))),
    isProfileInfoComplete:
      options?.context?.isProfileInfoComplete ??
      Boolean(existingCompData.name || existingCompData.displayName),
    adminVerdict:
      options?.context?.adminVerdict !== undefined
        ? options.context.adminVerdict
        : existingCompData.isVerified
        ? 'verified'
        : existingCompData.rejectionReason
        ? 'rejected'
        : null,
    rejectionReason:
      options?.context?.rejectionReason !== undefined
        ? options.context.rejectionReason
        : existingCompData.rejectionReason || null,
    verificationStatus:
      options?.context?.verificationStatus ||
      existingCompData.verificationStatus ||
      (options?.context?.verificationStarted ? 'under_review' : undefined),
    identityStatus:
      options?.context?.identityStatus ||
      existingCompData.identityStatus,
    isSuspended:
      options?.context?.isSuspended ??
      Boolean(existingCompData.accountStatus === 'Suspended' || existingCompData.isSuspended),
  };

  // 4. Calculate authoritative report and status
  const report = evaluateCompanionDocumentCompletion({
    documents: documentsMap || {},
    ...mergedContext,
  });

  const { profileStatus } = report;
  const isAdminAction = Boolean(options?.context?.adminVerdict);
  const safeProfileStatus = !isAdminAction && profileStatus === 'verified' ? 'under_review' : profileStatus;

  // 5. Persist to Firestore: 'companions/{companionId}'
  try {
    const companionRef = doc(db, 'companions', companionId);
    const updatePayload: Record<string, any> = {
      companionId,
      profileStatus: safeProfileStatus,
      lifecycleStatus: safeProfileStatus,
      verificationStatus:
        safeProfileStatus === 'verified'
          ? 'verified'
          : safeProfileStatus === 'under_review'
          ? 'under_review'
          : safeProfileStatus === 'rejected'
          ? 'rejected'
          : safeProfileStatus === 'documents_uploaded'
          ? 'submitted'
          : 'not_started',
      identityStatus:
        safeProfileStatus === 'verified'
          ? 'verified'
          : safeProfileStatus === 'under_review'
          ? 'pending'
          : safeProfileStatus === 'rejected'
          ? 'reupload_required'
          : safeProfileStatus === 'documents_uploaded'
          ? 'uploaded'
          : 'not_uploaded',
      documentsComplete: report.isValid,
      missingDocumentsCount: report.missingSidesCount,
      missingItems: report.missingItems,
      completionPercentage: report.completionPercentage,
      summaryMessage: report.summaryMessage,
      updatedAt: serverTimestamp(),
    };

    if (isAdminAction) {
      updatePayload.isVerified = safeProfileStatus === 'verified';
      updatePayload.verificationProgress = report.completionPercentage;
    }

    await setDoc(companionRef, updatePayload, { merge: true });
  } catch (err) {
    console.warn('syncCompanionLifecycleInFirestore: Failed writing to companions col:', err);
  }

  // 6. Optionally sync 'users/{userId}' document if companion user ID is provided
  const targetUserId = options?.userId || existingCompData.userId || existingCompData.uid;
  if (targetUserId) {
    try {
      const userRef = doc(db, 'users', targetUserId);
      const userUpdatePayload: Record<string, any> = {
        profileStatus: safeProfileStatus,
        lifecycleStatus: safeProfileStatus,
        documentsComplete: report.isValid,
        missingDocumentsCount: report.missingSidesCount,
        updatedAt: serverTimestamp(),
      };
      if (isAdminAction) {
        userUpdatePayload.companion_profile_status = safeProfileStatus === 'verified' ? 'Active' : 'Inactive';
        userUpdatePayload.isIdVerified = safeProfileStatus === 'verified';
        userUpdatePayload.verificationProgress = report.completionPercentage;
      }
      await setDoc(userRef, userUpdatePayload, { merge: true });
    } catch (err) {
      console.warn('syncCompanionLifecycleInFirestore: Failed updating users collection:', err);
    }
  }

  // 7. Update local cache for offline/refresh resilience
  try {
    const cacheKey = `pairup_verification_${companionId}`;
    const cachedProfile = localStorage.getItem(cacheKey);
    if (cachedProfile) {
      const parsed = JSON.parse(cachedProfile);
      localStorage.setItem(
        cacheKey,
        JSON.stringify({
          ...parsed,
          profileStatus,
          lifecycleStatus: profileStatus,
          isVerified: profileStatus === 'verified',
          verificationProgress: report.completionPercentage,
          documentsComplete: report.isValid,
          missingDocumentsCount: report.missingSidesCount,
        })
      );
    }
  } catch {}

  return { profileStatus, report };
}

/**
 * Dedicated companion profile update flow for Firestore.
 * Updates companion profile fields and recalculates authoritative lifecycle status.
 */
export async function updateCompanionProfileFlow(
  companionId: string,
  profileUpdates: Record<string, any>,
  options?: {
    userId?: string;
    documents?: Record<string, any>;
  }
): Promise<{
  profileStatus: CompanionProfileLifecycleStatus;
  report: CompanionDocumentCompletionReport;
}> {
  if (!companionId) {
    throw new Error('companionId is required for updateCompanionProfileFlow');
  }

  try {
    const companionRef = doc(db, 'companions', companionId);
    await setDoc(
      companionRef,
      {
        ...profileUpdates,
        companionId,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('updateCompanionProfileFlow: Failed updating companion document:', err);
  }

  return syncCompanionLifecycleInFirestore(companionId, {
    userId: options?.userId,
    documents: options?.documents,
    context: {
      isProfileInfoComplete:
        profileUpdates.isProfileInfoComplete ??
        Boolean(
          profileUpdates.name ||
          profileUpdates.displayName ||
          profileUpdates.aboutMe ||
          profileUpdates.bio
        ),
      selectedPrimaryDocType: profileUpdates.primaryDocType || profileUpdates.idType,
      selectedServices: profileUpdates.services,
      isDriverService:
        profileUpdates.isDriverService ??
        (profileUpdates.isDriverApproved ||
          profileUpdates.services?.some(
            (s: string) => typeof s === 'string' && s.toLowerCase().includes('driver')
          )),
      isSuspended: profileUpdates.isSuspended ?? profileUpdates.accountStatus === 'Suspended',
      adminVerdict: profileUpdates.adminVerdict,
      rejectionReason: profileUpdates.rejectionReason,
    },
  });
}

/**
 * Dedicated companion document update flow for Firestore.
 * Persists document part/side updates into 'companions/{companionId}/documents/{docType}'
 * and recalculates authoritative lifecycle status in Firestore.
 */
export async function updateCompanionDocumentFlow(
  companionId: string,
  docType: string,
  documentRecord: Record<string, any>,
  options?: {
    userId?: string;
  }
): Promise<{
  profileStatus: CompanionProfileLifecycleStatus;
  report: CompanionDocumentCompletionReport;
}> {
  if (!companionId || !docType) {
    throw new Error('companionId and docType are required for updateCompanionDocumentFlow');
  }

  try {
    const docRef = doc(db, 'companions', companionId, 'documents', docType);
    await setDoc(docRef, { ...documentRecord, documentType: docType, updatedAt: serverTimestamp() }, { merge: true });
  } catch (err) {
    console.warn(`updateCompanionDocumentFlow: Failed writing document ${docType}:`, err);
  }

  return syncCompanionLifecycleInFirestore(companionId, {
    userId: options?.userId,
  });
}

/**
 * Dedicated companion suspension update flow for Firestore.
 * Updates suspension state and recalculates authoritative lifecycle status.
 */
export async function setCompanionSuspensionFlow(
  companionId: string,
  isSuspended: boolean,
  reason?: string,
  options?: { userId?: string }
): Promise<{
  profileStatus: CompanionProfileLifecycleStatus;
  report: CompanionDocumentCompletionReport;
}> {
  try {
    const companionRef = doc(db, 'companions', companionId);
    await setDoc(
      companionRef,
      {
        isSuspended,
        accountStatus: isSuspended ? 'Suspended' : 'Active',
        suspensionReason: isSuspended ? reason || 'Administrative suspension' : null,
        suspendedAt: isSuspended ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('setCompanionSuspensionFlow error:', err);
  }

  return syncCompanionLifecycleInFirestore(companionId, {
    userId: options?.userId,
    context: {
      isSuspended,
      rejectionReason: isSuspended ? reason : null,
    },
  });
}

/**
 * Dedicated companion KYC verdict update flow for Firestore.
 * Sets admin approval or rejection verdict and recalculates authoritative lifecycle status.
 */
export async function setCompanionKycVerdictFlow(
  companionId: string,
  verdict: 'verified' | 'rejected',
  details?: {
    rejectionReason?: string;
    adminEmail?: string;
    userId?: string;
  }
): Promise<{
  profileStatus: CompanionProfileLifecycleStatus;
  report: CompanionDocumentCompletionReport;
}> {
  const isVerified = verdict === 'verified';
  try {
    const companionRef = doc(db, 'companions', companionId);
    await setDoc(
      companionRef,
      {
        isVerified,
        rejectionReason: isVerified ? null : details?.rejectionReason || 'Documents rejected during review',
        verifiedBy: isVerified ? details?.adminEmail || 'admin' : null,
        verifiedAt: isVerified ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('setCompanionKycVerdictFlow error:', err);
  }

  return syncCompanionLifecycleInFirestore(companionId, {
    userId: details?.userId,
    context: {
      adminVerdict: verdict,
      rejectionReason: isVerified ? null : details?.rejectionReason,
    },
  });
}

/**
 * Checks if companion is verified and authorized for public bookings.
 */
export function isCompanionVerifiedForBookings(status: CompanionProfileLifecycleStatus): boolean {
  return status === 'verified';
}

/**
 * Bundle default status manager utilities
 */
export const companionStatusManager = {
  mapFirestoreStateToLifecycleStatus,
  evaluateCompanionDocumentCompletion,
  syncCompanionLifecycleInFirestore,
  updateCompanionProfileFlow,
  updateCompanionDocumentFlow,
  setCompanionSuspensionFlow,
  setCompanionKycVerdictFlow,
  isCompanionVerifiedForBookings,
  LIFECYCLE_STATUS_METADATA,
};

export default companionStatusManager;
